/**
 * RepairAsteroidsGame — full-screen popup mini-game. Designed for viral
 * shareability: 5-sec learn curve, 60-sec round, Wordle-style emoji-grid
 * end card that pastes into Slack/iMessage/X with the story intact.
 *
 * Mechanic: classic Asteroids. Your "home" sits at the centre as a ship
 * that rotates to aim. Home-repair issues drop in from every edge,
 * drifting toward the centre. Move the mouse / drag your finger to aim;
 * tap, click, or hold space to fire bullets in the aim direction. Destroy
 * an asteroid before it reaches the home → $ Saved. Let one through →
 * lose a life and $ Lost climbs. After 60s OR all lives gone, surface
 * a shareable result card.
 *
 * Issues source: real `propertyIssues` when available, else seed list.
 */
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Issue, IssueUrgency } from '@/models/Issue';
import { getAverageIssueCost } from '@/utils/issues';

const HAUSER_LIME = '#c0ff02';
const HAUSER_BG = '#0d0d0d';
const HAUSER_TEXT = '#f3f3f3';
const HAUSER_MUTED = '#8B8680';

const URGENCY_COLOR: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: '#DB5282',
  [IssueUrgency.Urgent]: '#FF9A4D',
  [IssueUrgency.Moderate]: '#FFF981',
  [IssueUrgency.Low]: '#5AE9FF',
  [IssueUrgency.Monitor]: '#9A938A',
};
// Tiered low → high. Asteroids spawn at Monitor or Low and escalate up
// the chain as they age (and approach home). Monitor is included so
// freshly-spawned asteroids feel "out there" before becoming urgent.
const URGENCY_TIERS: IssueUrgency[] = [
  IssueUrgency.Monitor,
  IssueUrgency.Low,
  IssueUrgency.Moderate,
  IssueUrgency.Urgent,
  IssueUrgency.Critical,
];

const seed = (
  id: string,
  title: string,
  category: string,
  urgency: IssueUrgency,
  min: number,
  max: number,
): Issue => ({
  id,
  propertyId: 'demo',
  title,
  category,
  description: '',
  urgency,
  costEstimateMin: min,
  costEstimateMax: max,
});
const SEED_ISSUES: Issue[] = [
  seed('s1', 'Roof leak', 'Roofing', IssueUrgency.Moderate, 1500, 4500),
  seed('s2', 'Termite damage', 'Pest', IssueUrgency.Urgent, 3000, 9000),
  seed('s3', 'GFCI missing', 'Electrical', IssueUrgency.Low, 150, 350),
  seed('s4', 'Foundation crack', 'Structural', IssueUrgency.Moderate, 2000, 5000),
  seed('s5', 'Water heater EOL', 'Plumbing', IssueUrgency.Urgent, 1800, 2800),
  seed('s6', 'HVAC duct leak', 'HVAC', IssueUrgency.Moderate, 800, 1800),
  seed('s7', 'Loose handrail', 'Safety', IssueUrgency.Low, 200, 400),
  seed('s8', 'Window seal failure', 'Windows', IssueUrgency.Low, 500, 1500),
  seed('s9', 'Sub-floor rot', 'Structural', IssueUrgency.Urgent, 4000, 8000),
  seed('s10', 'Driveway settling', 'Concrete', IssueUrgency.Low, 1000, 3000),
  seed('s11', 'Chimney spalling', 'Masonry', IssueUrgency.Moderate, 1500, 3500),
  seed('s12', 'Sprinkler valve leak', 'Landscape', IssueUrgency.Low, 250, 600),
  seed('s13', 'Mold in attic', 'Air Quality', IssueUrgency.Critical, 3500, 7500),
  seed('s14', 'Garage door spring', 'Garage', IssueUrgency.Low, 250, 500),
];

const ROUND_MS = 60_000;
const SHIP_LIVES = 5;
// EQ-style health score for the game. Starts at the typical mid-good
// baseline and moves up/down based on what the player catches vs. misses.
const HEALTH_START = 72;
const HEALTH_MIN = 0;
const HEALTH_MAX = 100;
// Per-issue severity weight — matches the SEV_W table used by the
// MaintenanceReserveCard / Quick Apply flow in HomeownerHub, so the
// game's score math is the same calculus as the planning tool.
const URGENCY_SEV_W: Record<IssueUrgency, number> = {
  [IssueUrgency.Critical]: 2.5,
  [IssueUrgency.Urgent]: 1.5,
  [IssueUrgency.Moderate]: 1,
  [IssueUrgency.Low]: 0.5,
  [IssueUrgency.Monitor]: 0.25,
};
// Spending a Reserve costs a small flat amount; the real cost is set by
// the on-screen issue cost when the shockwave fires. Keep this as the
// minimum penalty for trivial situations.
const RESERVE_HEALTH_COST = 1;
const HOME_RADIUS = 36; // collision boundary at the centre
const SHIP_SIZE = 36; // half-extent of the Hauser "A" mark
// Mobile tuning — tighter screens need smaller sprites, slower closing
// speed (less reaction time), and the ship pushed off geometric center to
// sit between the top HUD and the bottom EQ Boost trigger.
const MOBILE_SPRITE_SCALE = 0.8;
const MOBILE_SPEED_SCALE = 0.7;
const MOBILE_TOP_PAD = 185; // height taken by HUD top bar + timer line
const MOBILE_BOTTOM_PAD = 95; // height taken by EQ Boost button + inset
const BULLET_SPEED = 0.95; // px/ms
const BULLET_R = 3;
const BULLET_COOLDOWN_MS = 170;
const ASTEROID_BASE_SPEED = 0.05; // px/ms
const ASTEROID_SPEED_VARIANCE = 0.03;
// Speed + HP scale with the asteroid's current urgency tier (which itself
// is driven by proximity to home). So a freshly-spawned Monitor drifts in
// lazily and pops in 1 shot, while a Critical that's about to hit is
// fast AND takes 4–5 hits — exactly when it's most dangerous.
const URGENCY_SPEED_MULT: Record<IssueUrgency, number> = {
  [IssueUrgency.Monitor]: 0.32,
  [IssueUrgency.Low]: 0.5,
  [IssueUrgency.Moderate]: 0.85,
  [IssueUrgency.Urgent]: 1.35,
  [IssueUrgency.Critical]: 1.95,
};
// Extra speed multiplier for asteroids that *started* at this urgency.
// An issue that's already Critical when it spawns is the alarm-on-day-one
// nightmare, so it moves harder than one that escalated late. Escalation
// from a calmer start uses the base URGENCY_SPEED_MULT only.
const STARTING_URGENCY_SPEED_BONUS: Partial<Record<IssueUrgency, number>> = {
  [IssueUrgency.Urgent]: 1.1,
  [IssueUrgency.Critical]: 1.2,
};
const URGENCY_HP_TARGET: Record<IssueUrgency, number> = {
  [IssueUrgency.Monitor]: 1,
  [IssueUrgency.Low]: 1,
  [IssueUrgency.Moderate]: 2,
  [IssueUrgency.Urgent]: 3,
  [IssueUrgency.Critical]: 4,
};
// Multiplied by baseRadius so smaller asteroids grow proportionally — at
// 0.00018, a 30-px asteroid roughly doubles in 6 seconds, very visibly.
const ASTEROID_GROWTH_FACTOR_PER_MS = 0.00018;
const SPAWN_INTERVAL_START_MS = 1700;
const SPAWN_INTERVAL_END_MS = 500;
// Ease-in on the spawn ramp — slightly gentler ramp keeps the early game
// playable but still ramps to a real squeeze.
const SPAWN_RAMP_EASE = 2;
// Global asteroid speed scalar that ramps from EARLY → LATE over the round
// using the same ease curve. Multiplied on top of URGENCY_SPEED_MULT.
const SPEED_RAMP_EARLY = 0.85;
const SPEED_RAMP_LATE = 1.15;

interface Asteroid {
  id: string;
  issue: Issue;
  x: number;
  y: number;
  vx: number; // toward home
  vy: number;
  baseRadius: number;
  ageMs: number;
  startUrgencyIdx: number;
  /** Distance from home at spawn — used to compute proximity-based escalation. */
  spawnDist: number;
  status: 'live' | 'fading';
  fadeMs?: number;
  baseCost: number;
  hp: number; // current hit points
  maxHp: number; // total at this moment (grows as it ages)
  hitFlashMs?: number; // brief white flash on bullet hit
  /** Set true once a shockwave has consumed this asteroid, so a single
   *  Reserve activation can't double-count it. */
  sweptByReserve?: boolean;
}

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  ageMs: number;
  lifeMs: number;
  size: number;
}

interface ScorePopup {
  x: number;
  y: number;
  text: string;
  color: string;
  ageMs: number;
}

interface ComboCallout {
  text: string;
  ageMs: number;
}

const POPUP_LIFE_MS = 900;
const PARTICLE_LIFE_MS = 700;
const CALLOUT_LIFE_MS = 1100;
// Reserve / Force Field — press SPACE to slow asteroids and bubble the
// ship for a few seconds. Costs $5K from net score. Limited uses.
const RESERVE_USES_PER_ROUND = 1;
// Reserve is now a one-shot shockwave that expands from the ship to the
// edges of the play area, wiping out anything in its path.
const RESERVE_WAVE_DURATION_MS = 1100;
// Width of the leading edge that counts as the destructive shockwave.
const RESERVE_WAVE_THICKNESS = 28;

interface RoundResult {
  asteroid: Asteroid;
  saved: number;
  lost: number;
}

export interface RepairAsteroidsGameProps {
  open: boolean;
  onClose: () => void;
  /** Real property issues; falls back to seed list. */
  issues?: Issue[];
  /** Shown on the share card when set. */
  homeLabel?: string;
  /** The property's current EQ Home Score (0–100). Used as the round's
   *  starting health. Falls back to HEALTH_START if null/undefined. */
  startScore?: number | null;
}

let asteroidCounter = 0;
const nextAsteroidId = () => `ast-${++asteroidCounter}`;

export const RepairAsteroidsGame = ({
  open,
  onClose,
  issues,
  homeLabel: _homeLabel,
  startScore,
}: RepairAsteroidsGameProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const computeHomeY = useCallback(
    (h: number) =>
      isMobile ? MOBILE_TOP_PAD + (h - MOBILE_TOP_PAD - MOBILE_BOTTOM_PAD) / 2 : h / 2,
    [isMobile],
  );

  const issuePool = useMemo<Issue[]>(() => {
    const pool = (issues ?? []).filter((i) => !i.resolved && getAverageIssueCost(i) > 0);
    return pool.length > 0 ? pool : SEED_ISSUES;
  }, [issues]);

  const asteroidsRef = useRef<Asteroid[]>([]);
  const bulletsRef = useRef<Bullet[]>([]);
  const aimRef = useRef<number>(-Math.PI / 2); // current rendered aim
  const aimTargetRef = useRef<number>(-Math.PI / 2); // where input wants aim
  const fireRef = useRef<boolean>(false);
  const keysRef = useRef<{ left: boolean; right: boolean; fire: boolean }>({
    left: false,
    right: false,
    fire: false,
  });
  const lastFireRef = useRef<number>(0);
  const startedAtRef = useRef<number>(0);
  const lastFrameRef = useRef<number>(0);
  const lastSpawnRef = useRef<number>(0);
  const livesRef = useRef<number>(SHIP_LIVES);
  const savedRef = useRef<number>(0);
  const lostRef = useRef<number>(0);
  // EQ-style health score for the round — starts at HEALTH_START and is
  // nudged up/down by kills, hits, and Reserve usage.
  const healthRef = useRef<number>(HEALTH_START);
  const startHealthRef = useRef<number>(HEALTH_START);
  // Sum of severity weights across the property's unresolved issues.
  // Used as the denominator in the same formula HomeownerHub uses to
  // compute projected score from resolutions.
  const totalSevWRef = useRef<number>(1);
  const resultsRef = useRef<RoundResult[]>([]);
  const screenShakeRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const popupsRef = useRef<ScorePopup[]>([]);
  const calloutRef = useRef<ComboCallout | null>(null);
  const reserveUsesRef = useRef<number>(RESERVE_USES_PER_ROUND);
  // Timestamp the current shockwave started (0 = no wave active).
  const reserveWaveStartRef = useRef<number>(0);

  const [gameState, setGameState] = useState<'intro' | 'playing' | 'over'>('intro');
  const [hudTick, setHudTick] = useState(0);
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Reset to the intro screen whenever the dialog is closed, so reopening
  // never lands the player in a stale mid-round or end-card state.
  useEffect(() => {
    if (!open) setGameState('intro');
  }, [open]);

  // Canvas DPR sizing.
  useEffect(() => {
    if (!open) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const c = canvasRef.current;
      if (c) {
        c.width = Math.floor(r.width * dpr);
        c.height = Math.floor(r.height * dpr);
        c.style.width = `${r.width}px`;
        c.style.height = `${r.height}px`;
        const ctx = c.getContext('2d');
        if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      setSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  const startRound = useCallback(() => {
    asteroidCounter = 0;
    asteroidsRef.current = [];
    bulletsRef.current = [];
    particlesRef.current = [];
    popupsRef.current = [];
    calloutRef.current = null;
    reserveUsesRef.current = RESERVE_USES_PER_ROUND;
    reserveWaveStartRef.current = 0;
    livesRef.current = SHIP_LIVES;
    savedRef.current = 0;
    lostRef.current = 0;
    const seed =
      typeof startScore === 'number' && Number.isFinite(startScore)
        ? Math.max(HEALTH_MIN, Math.min(HEALTH_MAX, startScore))
        : HEALTH_START;
    healthRef.current = seed;
    startHealthRef.current = seed;
    // Snapshot the property's total severity weight for the round.
    // Mirrors HomeownerHub's totalW = Σ SEV_W over unresolved issues.
    const allUnres = issuePool;
    const totalW = allUnres.reduce(
      (s, i) => s + (URGENCY_SEV_W[i.urgency] ?? 0),
      0,
    );
    totalSevWRef.current = totalW > 0 ? totalW : 1;
    resultsRef.current = [];
    screenShakeRef.current = 0;
    startedAtRef.current = performance.now();
    lastFrameRef.current = startedAtRef.current;
    lastSpawnRef.current = startedAtRef.current;
    lastFireRef.current = 0;
    setGameState('playing');
  }, [startScore]);

  const restart = useCallback(() => startRound(), [startRound]);

  // EQ-style health movement on kill / hit. Caught early = full credit;
  // caught late or missed entirely = bigger penalty. Combo multiplier is
  // applied by the caller (kill sites only).
  // Same calculus as MaintenanceReserveCard's projected-score formula
  //   projected = startScore + (resolvedW / totalW) * (100 − startScore)
  // but with a symmetric headroom of min(startScore, 100−startScore) so a
  // kill and a hit on the same urgency move the score by the same
  // magnitude regardless of where the starting score sits in 0–100.
  // (The asymmetric variant felt punishing for high-EQ properties.)
  const killHealthDelta = (a: Asteroid): number => {
    const w = URGENCY_SEV_W[a.issue.urgency] ?? 0;
    const headroom = Math.min(
      startHealthRef.current - HEALTH_MIN,
      HEALTH_MAX - startHealthRef.current,
    );
    return (w / totalSevWRef.current) * headroom;
  };
  const hitHealthDelta = (a: Asteroid): number => {
    const w = URGENCY_SEV_W[a.issue.urgency] ?? 0;
    const headroom = Math.min(
      startHealthRef.current - HEALTH_MIN,
      HEALTH_MAX - startHealthRef.current,
    );
    return (w / totalSevWRef.current) * headroom;
  };
  const applyHealthDelta = (delta: number) => {
    healthRef.current = Math.max(
      HEALTH_MIN,
      Math.min(HEALTH_MAX, healthRef.current + delta),
    );
  };

  // Reserve — costs a few health points, fires a shockwave that expands
  // from the ship to the edges of the play area, wiping out anything in
  // its path.
  const triggerReserve = useCallback(() => {
    if (gameState !== 'playing') return;
    if (reserveUsesRef.current <= 0) return;
    const now = performance.now();
    // Block re-trigger while a wave is still expanding.
    if (now - reserveWaveStartRef.current < RESERVE_WAVE_DURATION_MS) return;
    reserveUsesRef.current -= 1;
    healthRef.current = Math.max(HEALTH_MIN, healthRef.current - RESERVE_HEALTH_COST);
    reserveWaveStartRef.current = now;
    calloutRef.current = { text: 'EQ BOOST', ageMs: 0 };
  }, [gameState]);

  // Spawn an asteroid at a random screen edge, moving toward the centre.
  const spawnAsteroid = useCallback(
    (now: number) => {
      const w = size.w;
      const h = size.h;
      if (w <= 0 || h <= 0 || issuePool.length === 0) return;
      const issue = issuePool[Math.floor(Math.random() * issuePool.length)];
      const baseCost = getAverageIssueCost(issue);
      const baseRadius = Math.max(20, Math.min(44, 20 + Math.sqrt(baseCost) * 0.18));
      // Pick an edge.
      const edge = Math.floor(Math.random() * 4);
      let x = 0;
      let y = 0;
      if (edge === 0) {
        x = Math.random() * w;
        y = -baseRadius;
      } else if (edge === 1) {
        x = w + baseRadius;
        y = Math.random() * h;
      } else if (edge === 2) {
        x = Math.random() * w;
        y = h + baseRadius;
      } else {
        x = -baseRadius;
        y = Math.random() * h;
      }
      const cx = w / 2;
      const cy = computeHomeY(h);
      const dx = cx - x;
      const dy = cy - y;
      const dist = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      // Start tier comes from the actual issue's real urgency — Critical
      // / Urgent / Big-Ticket items spawn fast AND beefy, while Low /
      // Monitor items drift in lazy as 1-shot kills. The inspection
      // report literally drives the difficulty curve.
      const realIdx = URGENCY_TIERS.indexOf(issue.urgency);
      const startUrgencyIdx = realIdx >= 0 ? realIdx : 0;
      const startTier = URGENCY_TIERS[startUrgencyIdx];
      const speed =
        (ASTEROID_BASE_SPEED + Math.random() * ASTEROID_SPEED_VARIANCE) *
        URGENCY_SPEED_MULT[startTier];
      const startHp = URGENCY_HP_TARGET[startTier];
      asteroidsRef.current.push({
        id: nextAsteroidId(),
        issue,
        x,
        y,
        vx: (dx / dist) * speed,
        vy: (dy / dist) * speed,
        baseRadius,
        ageMs: 0,
        startUrgencyIdx,
        spawnDist: dist,
        status: 'live',
        baseCost,
        hp: startHp,
        maxHp: startHp,
      });
      lastSpawnRef.current = now;
    },
    [size.w, size.h, issuePool, computeHomeY],
  );

  const fireBullet = useCallback(() => {
    const cx = size.w / 2;
    const cy = computeHomeY(size.h);
    const shipSize = isMobile ? SHIP_SIZE * MOBILE_SPRITE_SCALE : SHIP_SIZE;
    const angle = aimRef.current;
    bulletsRef.current.push({
      x: cx + Math.cos(angle) * (shipSize + 4),
      y: cy + Math.sin(angle) * (shipSize + 4),
      vx: Math.cos(angle) * BULLET_SPEED,
      vy: Math.sin(angle) * BULLET_SPEED,
    });
  }, [size.w, size.h, computeHomeY, isMobile]);

  // Aim follows pointer (sets target; aim lerps toward it in the loop).
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (gameState !== 'playing') return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = size.w / 2;
      const cy = computeHomeY(size.h);
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      aimTargetRef.current = Math.atan2(py - cy, px - cx);
    },
    [gameState, size.w, size.h, computeHomeY],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (gameState !== 'playing') return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = size.w / 2;
      const cy = computeHomeY(size.h);
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      aimTargetRef.current = Math.atan2(py - cy, px - cx);
      fireRef.current = true;
    },
    [gameState, size.w, size.h, computeHomeY],
  );

  const handlePointerUp = useCallback(() => {
    fireRef.current = false;
  }, []);

  // Keyboard — arrows / A-D rotate aim, SPACE engages the Reserve
  // force field (autofire so no key needed for shooting), Esc closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent, down: boolean) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') keysRef.current.left = down;
      else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') keysRef.current.right = down;
      else if (e.key === ' ' || e.code === 'Space') {
        if (down) triggerReserve();
        e.preventDefault();
      } else if (e.key === 'Escape' && down) {
        onClose();
      }
    };
    const dn = (e: KeyboardEvent) => onKey(e, true);
    const up = (e: KeyboardEvent) => onKey(e, false);
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', dn);
      window.removeEventListener('keyup', up);
    };
  }, [open, onClose, triggerReserve]);

  // Game loop.
  useEffect(() => {
    if (!open || gameState !== 'playing') return undefined;
    let raf = 0;
    const ctx = canvasRef.current?.getContext('2d');
    const KEY_ROTATE_SPEED = 0.005; // rad/ms

    const tick = (now: number) => {
      const dt = Math.min(40, now - lastFrameRef.current);
      lastFrameRef.current = now;
      const elapsed = now - startedAtRef.current;
      const remaining = Math.max(0, ROUND_MS - elapsed);
      const w = size.w;
      const h = size.h;
      const cx = w / 2;
      const cy = computeHomeY(h);

      // Keyboard rotation nudges the *target* angle; aim lerps toward
      // target each frame, smoothing both keyboard and mouse input.
      if (keysRef.current.left) aimTargetRef.current -= KEY_ROTATE_SPEED * dt;
      if (keysRef.current.right) aimTargetRef.current += KEY_ROTATE_SPEED * dt;
      // Lerp aim toward target along the shortest angular path.
      const TWO_PI = Math.PI * 2;
      let delta = ((aimTargetRef.current - aimRef.current + Math.PI) % TWO_PI) - Math.PI;
      if (delta < -Math.PI) delta += TWO_PI;
      // 0.22 lerp per frame at 60fps ≈ ~13ms half-life — feels responsive
      // but smooths jitter and adds a satisfying ease-out on key release.
      aimRef.current += delta * Math.min(1, 0.22 * (dt / 16));

      // Continuous auto-fire — Galaga / Invaders style. Player just
      // aims; ship pews on its own cooldown.
      if (now - lastFireRef.current >= BULLET_COOLDOWN_MS) {
        fireBullet();
        lastFireRef.current = now;
      }

      // Spawn cadence.
      const rampT = Math.max(0, Math.min(1, elapsed / ROUND_MS));
      const easedRamp = Math.pow(rampT, SPAWN_RAMP_EASE);
      const spawnInterval =
        SPAWN_INTERVAL_START_MS -
        (SPAWN_INTERVAL_START_MS - SPAWN_INTERVAL_END_MS) * easedRamp;
      const globalSpeedScale =
        (SPEED_RAMP_EARLY + (SPEED_RAMP_LATE - SPEED_RAMP_EARLY) * easedRamp) *
        (isMobile ? MOBILE_SPEED_SCALE : 1);
      if (now - lastSpawnRef.current >= spawnInterval) {
        spawnAsteroid(now);
      }

      // Reserve shockwave — current radius (0 if no wave is active). The
      // wave eases out from the ship to the corner of the play area over
      // RESERVE_WAVE_DURATION_MS, then dissipates.
      const waveAge = reserveWaveStartRef.current
        ? now - reserveWaveStartRef.current
        : Infinity;
      const waveActive = waveAge >= 0 && waveAge < RESERVE_WAVE_DURATION_MS;
      const waveMaxRadius = Math.sqrt(cx * cx + cy * cy) + RESERVE_WAVE_THICKNESS;
      const waveT = waveActive ? Math.min(1, waveAge / RESERVE_WAVE_DURATION_MS) : 0;
      // Ease-out cubic so the wave bursts out fast, then trails.
      const waveEased = 1 - Math.pow(1 - waveT, 3);
      const waveRadius = waveActive ? waveEased * waveMaxRadius : 0;

      // Asteroids advance + collide with home + tier-driven speed/HP.
      for (const a of asteroidsRef.current) {
        if (a.status === 'live') {
          // Compute proximity → current tier first; that drives both
          // speed multiplier and target HP this frame.
          const dxh = a.x - cx;
          const dyh = a.y - cy;
          const distFromHome = Math.sqrt(dxh * dxh + dyh * dyh);
          const proximity =
            a.spawnDist > 0
              ? Math.max(0, Math.min(1, 1 - distFromHome / a.spawnDist))
              : 0;
          const tier = effectiveTier(a, proximity);
          const speedMult = URGENCY_SPEED_MULT[tier];
          const startTier = URGENCY_TIERS[a.startUrgencyIdx];
          const startBonus = STARTING_URGENCY_SPEED_BONUS[startTier] ?? 1;
          a.x += a.vx * speedMult * startBonus * globalSpeedScale * dt;
          a.y += a.vy * speedMult * startBonus * globalSpeedScale * dt;
          a.ageMs += dt;
          // Tier-driven HP regen: maxHp climbs toward the current tier's
          // target. So an Urgent that escalates to Critical near the home
          // gains an extra hit point along the way.
          const targetHp = URGENCY_HP_TARGET[tier];
          if (a.maxHp < targetHp) {
            const add = targetHp - a.maxHp;
            a.maxHp += add;
            a.hp += add;
          }
          if (a.hitFlashMs && a.hitFlashMs > 0) {
            a.hitFlashMs = Math.max(0, a.hitFlashMs - dt);
          }
          // Reserve shockwave: any asteroid the leading edge has passed
          // over (and that hasn't already been swept by this wave) is
          // destroyed. One wave can consume each asteroid at most once.
          if (
            waveActive &&
            !a.sweptByReserve &&
            distFromHome <= waveRadius + asteroidRadius(a) * (isMobile ? MOBILE_SPRITE_SCALE : 1)
          ) {
            a.sweptByReserve = true;
            const points = killHealthDelta(a);
            applyHealthDelta(points);
            const baseReward = savedReward(a, proximity);
            savedRef.current += baseReward;
            resultsRef.current.push({ asteroid: a, saved: baseReward, lost: 0 });
            popupsRef.current.push({
              x: a.x,
              y: a.y,
              text: `↑${points.toFixed(1)}`,
              color: HAUSER_LIME,
              ageMs: 0,
            });
            spawnParticles(particlesRef.current, a.x, a.y, HAUSER_LIME, 18);
            spawnParticles(particlesRef.current, a.x, a.y, URGENCY_COLOR[tier], 10);
            a.status = 'fading';
            a.fadeMs = 0;
          } else if (
            distFromHome <=
            (asteroidRadius(a) + HOME_RADIUS) * (isMobile ? MOBILE_SPRITE_SCALE : 1)
          ) {
            const points = hitHealthDelta(a);
            applyHealthDelta(-points);
            const dmg = hitDamage(a);
            lostRef.current += dmg;
            livesRef.current = Math.max(0, livesRef.current - 1);
            screenShakeRef.current = 14;
            resultsRef.current.push({ asteroid: a, saved: 0, lost: dmg });
            spawnParticles(particlesRef.current, a.x, a.y, '#DB5282', 14);
            popupsRef.current.push({
              x: a.x,
              y: a.y,
              text: `↓${points.toFixed(1)}`,
              color: '#DB5282',
              ageMs: 0,
            });
            a.status = 'fading';
            a.fadeMs = 0;
          }
        } else if (a.status === 'fading') {
          a.fadeMs = (a.fadeMs ?? 0) + dt;
        }
      }
      asteroidsRef.current = asteroidsRef.current.filter(
        (a) => !(a.status === 'fading' && (a.fadeMs ?? 0) > 350),
      );

      // Bullets advance + cull off-screen.
      for (const b of bulletsRef.current) {
        b.x += b.vx * dt;
        b.y += b.vy * dt;
      }
      bulletsRef.current = bulletsRef.current.filter(
        (b) => b.x > -20 && b.x < w + 20 && b.y > -20 && b.y < h + 20,
      );

      // Bullet ↔ asteroid collisions — bullets chip HP; only kill at 0.
      for (const b of bulletsRef.current) {
        for (const a of asteroidsRef.current) {
          if (a.status !== 'live') continue;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const r = asteroidRadius(a) * (isMobile ? MOBILE_SPRITE_SCALE : 1) + BULLET_R;
          if (dx * dx + dy * dy <= r * r) {
            a.hp -= 1;
            a.hitFlashMs = 120;
            // Small lime sparks on every hit.
            spawnParticles(particlesRef.current, b.x, b.y, HAUSER_LIME, 4);
            if (a.hp <= 0) {
              a.status = 'fading';
              a.fadeMs = 0;
              // Compute proximity at the moment of kill so the reward
              // reflects how late we caught it.
              const dx2 = a.x - cx;
              const dy2 = a.y - cy;
              const distNow = Math.sqrt(dx2 * dx2 + dy2 * dy2);
              const prox =
                a.spawnDist > 0
                  ? Math.max(0, Math.min(1, 1 - distNow / a.spawnDist))
                  : 0;
              const baseReward = savedReward(a, prox);
              const points = killHealthDelta(a);
              applyHealthDelta(points);
              savedRef.current += baseReward;
              resultsRef.current.push({ asteroid: a, saved: baseReward, lost: 0 });
              popupsRef.current.push({
                x: a.x,
                y: a.y,
                text: `↑${points.toFixed(1)}`,
                color: HAUSER_LIME,
                ageMs: 0,
              });
              const tier = effectiveTier(a, prox);
              spawnParticles(particlesRef.current, a.x, a.y, URGENCY_COLOR[tier], 14);
            }
            b.x = -100;
            break;
          }
        }
      }
      bulletsRef.current = bulletsRef.current.filter(
        (b) => b.x > -20 && b.x < w + 20 && b.y > -20 && b.y < h + 20,
      );

      // Advance particles + popups + callout.
      for (const p of particlesRef.current) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.ageMs += dt;
        // Slight drag so particles slow down over their lifetime.
        p.vx *= 0.97;
        p.vy *= 0.97;
      }
      particlesRef.current = particlesRef.current.filter((p) => p.ageMs < p.lifeMs);

      for (const pop of popupsRef.current) {
        pop.ageMs += dt;
        pop.y -= 0.04 * dt; // drift up
      }
      popupsRef.current = popupsRef.current.filter((p) => p.ageMs < POPUP_LIFE_MS);

      if (calloutRef.current) {
        calloutRef.current.ageMs += dt;
        if (calloutRef.current.ageMs >= CALLOUT_LIFE_MS) calloutRef.current = null;
      }

      // Render.
      if (ctx) {
        // For HUD purposes only: 1 while the wave is expanding, 0 otherwise.
        const reserveFrac = waveActive ? 1 - waveT : 0;
        renderFrame(
          ctx,
          asteroidsRef.current,
          bulletsRef.current,
          aimRef.current,
          size,
          screenShakeRef.current,
          particlesRef.current,
          popupsRef.current,
          calloutRef.current,
          reserveFrac,
          reserveUsesRef.current,
          waveActive ? waveRadius : 0,
          waveActive ? waveT : 0,
          isMobile,
          cy,
          isMobile ? MOBILE_SPRITE_SCALE : 1,
        );
      }
      if (screenShakeRef.current > 0) {
        screenShakeRef.current = Math.max(0, screenShakeRef.current - dt * 0.05);
      }

      if (Math.floor(elapsed / 100) !== hudTick) setHudTick(Math.floor(elapsed / 100));

      if (remaining <= 0 || livesRef.current <= 0) {
        setGameState('over');
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, gameState, fireBullet, spawnAsteroid, size, hudTick]);

  if (!open) return null;

  const elapsed = gameState === 'playing' ? performance.now() - startedAtRef.current : 0;
  const remaining = Math.max(0, ROUND_MS - elapsed);
  const remainingSec = Math.ceil(remaining / 1000);

  return createPortal(
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        background: HAUSER_BG,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
        color: HAUSER_TEXT,
        userSelect: 'none',
        touchAction: 'none',
      }}>
      {/* Top arcade bar — lives top-left, NET SCORE centered, close on
          the right. Timer is the lime bar along the very top edge.
          Hidden once the round is over so the end-card stands alone. */}
      {gameState === 'playing' && (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 12,
          padding: isMobile ? '29px 16px 18px' : '44px 16px 18px',
          background: 'rgba(13,13,13,0.7)',
          backdropFilter: 'blur(14px) saturate(160%)',
          WebkitBackdropFilter: 'blur(14px) saturate(160%)',
          fontFamily: "'Manrope', sans-serif",
          pointerEvents: 'none',
        }}>
        {/* Left spacer — lives moved to under the EQ ring. */}
        <div />


        {/* Center: HOME SCORE donut ring — mirrors the ScoreRing used in
            the home hub / repair planning module so the metaphor of
            improving your real score reads instantly. */}
        {(() => {
          const score = Math.round(healthRef.current);
          const start = Math.round(startHealthRef.current);
          const delta = score - start;
          const ringSize = 96;
          // Match the repair-planning ScoreRing's strokeW/size ratio (9/175 ≈ 0.051).
          const strokeW = 5;
          const r = (ringSize - strokeW * 2) / 2;
          const circ = 2 * Math.PI * r;
          const pct = Math.max(0, Math.min(100, score)) / 100;
          const offset = circ - pct * circ;
          // Same score-to-color mapping used by the home-hub HomeScoreCard
          // and the repair planning ring, so the indicator color shifts as
          // the player's home score climbs/drops.
          const scoreToColor = (s: number) =>
            s >= 86
              ? '#f3f3f3'
              : s >= 77
              ? '#5AE9FF'
              : s >= 68
              ? '#FFF981'
              : s >= 59
              ? '#FF9A4D'
              : '#DB5282';
          const ringColor = scoreToColor(score);
          // Up + lime when the cumulative score climbed; down + pink when
          // it took a net hit. Always reflects net change from start.
          const deltaColor =
            delta > 0 ? '#C0FF02' : delta < 0 ? '#DB5282' : 'rgba(187,183,175,0.7)';
          const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '';
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                justifySelf: 'center',
              }}>
              <div style={{ position: 'relative', width: ringSize, height: ringSize }}>
              <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={r}
                  fill="none"
                  stroke="#383838"
                  strokeWidth={strokeW}
                />
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={r}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth={strokeW}
                  strokeDasharray={circ}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                  style={{
                    transition:
                      'stroke-dashoffset 350ms cubic-bezier(0.4,0,0.2,1), stroke 200ms linear',
                  }}
                />
              </svg>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: "'Manrope', sans-serif",
                }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#bbb7af',
                    lineHeight: 1,
                    opacity: 0.85,
                    marginBottom: 2,
                  }}>
                  EQ
                </div>
                <div
                  style={{
                    fontSize: ringSize * 0.28,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: ringColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {score}
                </div>
                {delta !== 0 && (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      marginTop: 3,
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    <span style={{ color: deltaColor }}>{arrow}</span>
                    <span style={{ color: '#f3f3f3' }}>{Math.abs(delta)}</span>
                  </div>
                )}
              </div>
              </div>
              {/* Lives row under the ring — tic-tac pills matching the
                  Offers indicator on the Clients/Deals page. */}
              <div style={{ display: 'inline-flex', gap: 3, alignItems: 'center', marginTop: 10 }}>
                {Array.from({ length: SHIP_LIVES }).map((_, i) => (
                  <span
                    key={i}
                    style={{
                      width: 8,
                      height: 4,
                      borderRadius: 2,
                      background:
                        i < livesRef.current ? '#bbb7af' : 'rgba(187,183,175,0.22)',
                      display: 'inline-block',
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })()}

        {/* Right grid cell — empty spacer; the close X is absolute-
            positioned at the dialog corner below. */}
        <div />
      </div>
      )}

      {/* Gameplay close — pinned to the dialog's top-right corner, same
          spot as the intro X for consistency. */}
      {gameState === 'playing' && (
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 12,
            background: 'rgba(38,38,38,0.6)',
            color: HAUSER_TEXT,
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 999,
            width: 32,
            height: 32,
            cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1,
            pointerEvents: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}>
          ×
        </button>
      )}

      {/* Timer bar — sits directly under the top bar, depleting right→
          left as the round runs out. Doubles as the bar's bottom border
          and the round's countdown. Hidden once the round is over. */}
      {gameState === 'playing' && (
      <div
        style={{
          position: 'absolute',
          top: isMobile ? 171 : 186,
          left: 0,
          right: 0,
          height: 3,
          zIndex: 11,
          background: 'rgba(255,255,255,0.04)',
          pointerEvents: 'none',
        }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            // Width shrinks as time runs out (right edge eats inward).
            width: `${Math.max(0, (remaining / ROUND_MS) * 100)}%`,
            background:
              remainingSec <= 10
                ? '#DB5282'
                : '#bbb7af',
            boxShadow:
              remainingSec <= 10
                ? '0 0 8px rgba(219,82,130,0.6)'
                : 'none',
            transition: 'background 0.2s, box-shadow 0.2s',
          }}
        />
      </div>
      )}

      {/* Canvas — absolute-positioned to fill the container reliably,
          regardless of how many absolute-positioned HUD siblings exist
          (flex:1 + multiple absolute siblings + imperative style writes
          from measure() were producing inconsistent sizing). */}
      <canvas
        ref={canvasRef}
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'block',
          cursor: gameState === 'playing' ? 'crosshair' : 'default',
        }}
      />

      {/* Intro */}
      {/* Mobile EQ Boost trigger — pulsating bolt-in-circle bottom-center.
          Replaces the keyboard hint on touch since there's no spacebar. */}
      {gameState === 'playing' && isMobile && (
        <>
          {/* Wrapper handles the centering transform; the inner button
              owns the pulse animation so its scale doesn't fight the
              translateX(-50%). */}
          {/* Same subtle spark used on the EQ Boost button on the
              repair-planning card — quick scale + glow flash every 2.6s,
              with long rest periods in between (not a constant pulse). */}
          <style>{`
            @keyframes mobileBoostPulse {
              0%, 80%, 100% {
                transform: scale(1);
                filter: drop-shadow(0 0 0 rgba(192,255,2,0));
              }
              88% {
                transform: scale(1.25) rotate(-4deg);
                filter: drop-shadow(0 0 5px rgba(192,255,2,0.9));
              }
              94% {
                transform: scale(1.08) rotate(2deg);
                filter: drop-shadow(0 0 3px rgba(192,255,2,0.5));
              }
            }
          `}</style>
          <div
            style={{
              position: 'absolute',
              bottom: 28,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 12,
            }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                triggerReserve();
              }}
              disabled={reserveUsesRef.current <= 0}
              aria-label="EQ Boost"
              style={{
                width: 51,
                height: 51,
                borderRadius: '50%',
                background: 'transparent',
                border: `1.5px solid ${
                  reserveUsesRef.current > 0 ? HAUSER_LIME : 'rgba(187,183,175,0.35)'
                }`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: reserveUsesRef.current > 0 ? 'pointer' : 'not-allowed',
                padding: 0,
                animation:
                  reserveUsesRef.current > 0
                    ? 'mobileBoostPulse 2.6s ease-out infinite'
                    : undefined,
                transformOrigin: 'center',
              }}>
              <svg width="24" height="24" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M7 1L2 7h3l-1 4 5-6H6l1-4Z"
                  stroke={
                    reserveUsesRef.current > 0 ? HAUSER_LIME : 'rgba(187,183,175,0.7)'
                  }
                  strokeWidth="0.9"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </>
      )}

      {gameState === 'intro' && (
        <Overlay>
          {/* Close — top-right, traditional dialog position. */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'rgba(38,38,38,0.6)',
              color: HAUSER_TEXT,
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 999,
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
              fontSize: 16,
              fontWeight: 500,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}>
            ×
          </button>
          {/* Large Hauser "A" mark — same path as the in-game ship. */}
          <svg
            width="168"
            height="168"
            viewBox="0 0 100 100"
            aria-hidden
            style={{ marginBottom: 16 }}>
            <path
              d={
                'M56.2133 22.2267H43.7733L31.4266 77.7867H68.56L56.2133 22.2267Z' +
                'M44.2266 67.7067L49.7066 40.3067L49.96 38.32L50.2133 40.3067' +
                'L55.76 67.7067H44.24H44.2266Z'
              }
              fill="#333333"
              fillRule="evenodd"
            />
          </svg>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: HAUSER_LIME,
              marginBottom: 10,
            }}>
            Defend Your Home
          </div>
          <h1
            style={{
              fontSize: 44,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              margin: 0,
              lineHeight: 1.05,
            }}>
            Shoot the repairs.
          </h1>
          <p
            style={{
              fontSize: 14,
              color: HAUSER_MUTED,
              margin: '14px 0 22px',
              maxWidth: 480,
              lineHeight: 1.55,
            }}>
              Aim with your cursor; auto-fire takes care of the rest. Every issue you
              blast lifts your EQ score; misses drag it down.{' '}
              {isMobile ? (
                <>
                  Tap the{' '}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'rgba(192,255,2,0.18)',
                      border: `1px solid ${HAUSER_LIME}`,
                      verticalAlign: 'middle',
                      margin: '0 2px',
                    }}>
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
                      <path
                        d="M7 1L2 7h3l-1 4 5-6H6l1-4Z"
                        stroke={HAUSER_LIME}
                        strokeWidth="0.9"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>{' '}
                  to deploy your{' '}
                  <span style={{ color: HAUSER_LIME, fontWeight: 600 }}>EQ Boost</span>
                  {' '}— a one-shot shield that wipes the screen.
                </>
              ) : (
                <>
                  Hit{' '}
                  <span style={{ color: '#F3F3F3', fontWeight: 600 }}>SPACE</span> to deploy
                  your <span style={{ color: HAUSER_LIME, fontWeight: 600 }}>EQ Boost</span>
                  {' '}— a one-shot shield that wipes the screen.
                </>
              )}
          </p>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: HAUSER_MUTED,
              margin: '0 0 42px',
            }}>
            Auto-fires
            <span style={{ color: 'rgba(187,183,175,0.4)', margin: '0 10px' }}>|</span>
            60s · 5 Lives
          </div>
          {/* EQ Boost lockup — same component the player sees in-game,
              ported to JSX so the intro reads as a faithful preview.
              Hidden on mobile (no keyboard); the inline icon in the
              blurb already conveys the affordance. */}
          {!isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              margin: '0 0 26px',
              fontFamily: "'Manrope', sans-serif",
            }}>
            {/* Lightning bolt in a circle. */}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'rgba(192,255,2,0.10)',
                border: `1px solid ${HAUSER_LIME}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M7 1L2 7h3l-1 4 5-6H6l1-4Z"
                  stroke={HAUSER_LIME}
                  strokeWidth="0.9"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {/* EQ BOOST label, 30% letter-spacing. */}
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '4.5px',
                color: '#F3F3F3',
              }}>
              EQ BOOST
            </div>
            {/* Mini keyboard, spacebar highlighted. */}
            <KeyboardHintHTML />
          </div>
          )}
          <button
            onClick={startRound}
            style={{
              marginTop: 20,
              background: HAUSER_LIME,
              color: '#1e1e1e',
              border: '1px solid #c0ff02',
              borderRadius: 12,
              padding: '0 28px',
              height: 40,
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textTransform: 'none',
              cursor: 'pointer',
              fontFamily: "'Manrope', sans-serif",
              lineHeight: 1.2,
            }}>
            Launch
          </button>
        </Overlay>
      )}

      {gameState === 'over' && (
        <EndScreen
          results={resultsRef.current}
          startScore={startHealthRef.current}
          endScore={healthRef.current}
          onRestart={restart}
          onClose={onClose}
        />
      )}
    </div>,
    document.body,
  );
};

// HTML port of drawKeyboardHint — same dim outline of all keys with the
// spacebar highlighted, used on the intro overlay so the player can see
// the EQ Boost affordance before the round starts. The spacebar pulses
// to draw attention to the trigger.
const KeyboardHintHTML = () => {
  const keyW = 7;
  const keyH = 7;
  const gap = 1.5;
  const rowGap = 1.5;
  const rows: number[][] = [
    Array(10).fill(1),
    [1.4, ...Array(9).fill(1)],
    [1.7, ...Array(8).fill(1), 1.7],
    [1.2, 1, 5.6, 1, 1.2],
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: rowGap,
      }}>
      <style>{`
        @keyframes spaceBarPulse {
          0%, 100% {
            background: rgba(192,255,2,0.12);
            box-shadow: 0 0 0 0 rgba(192,255,2,0.55);
          }
          50% {
            background: rgba(192,255,2,0.34);
            box-shadow: 0 0 0 4px rgba(192,255,2,0);
          }
        }
        .eq-spacebar-pulse {
          animation: spaceBarPulse 1.5s ease-in-out infinite;
        }
      `}</style>
      {rows.map((widths, ri) => (
        <div key={ri} style={{ display: 'flex', gap }}>
          {widths.map((u, ki) => {
            const isSpace = ri === rows.length - 1 && ki === 2;
            return (
              <div
                key={ki}
                className={isSpace ? 'eq-spacebar-pulse' : undefined}
                style={{
                  width: u * keyW,
                  height: keyH,
                  borderRadius: 1.6,
                  background: isSpace
                    ? 'rgba(192,255,2,0.18)'
                    : 'rgba(255,255,255,0.04)',
                  border: isSpace
                    ? `1px solid ${HAUSER_LIME}`
                    : '1px solid rgba(255,255,255,0.16)',
                  boxSizing: 'border-box',
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

const Overlay = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      background: 'rgba(13,13,13,0.86)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      zIndex: 5,
      padding: 24,
    }}>
    {children}
  </div>
);

const EndScreen = ({
  results,
  startScore,
  endScore,
  onRestart,
  onClose,
}: {
  results: RoundResult[];
  startScore: number;
  endScore: number;
  onRestart: () => void;
  onClose: () => void;
}) => {
  const fixedCount = results.filter((r) => r.saved > 0).length;
  const hitCount = results.filter((r) => r.lost > 0).length;
  const start = Math.round(startScore);
  const end = Math.round(endScore);
  const delta = end - start;

  const deltaColor = delta > 0 ? '#C0FF02' : delta < 0 ? '#DB5282' : 'rgba(187,183,175,0.7)';
  const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '';

  return (
    <Overlay>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#bbb7af',
          marginBottom: 14,
        }}>
        Round complete
      </div>
      {/* Big EQ summary line: "49 → 60  ↑11" */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 14,
          fontFamily: "'Manrope', sans-serif",
        }}>
        <div
          style={{
            fontSize: 48,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            color: '#f3f3f3',
            fontVariantNumeric: 'tabular-nums',
          }}>
          {start}
          <span style={{ color: HAUSER_MUTED, margin: '0 10px' }}>→</span>
          {end}
        </div>
        {delta !== 0 && (
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
            <span style={{ color: deltaColor }}>{arrow}</span>
            <span style={{ color: '#f3f3f3' }}>{Math.abs(delta)}</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: HAUSER_MUTED, marginTop: 10 }}>
        {delta > 0
          ? `Improved your home EQ by ${Math.abs(delta)} pts`
          : delta < 0
          ? `Eroded your home EQ by ${Math.abs(delta)} pts`
          : `Held your home EQ at ${end}`}
      </div>
      {/* Visual grid uses Hauser app colours (lime saved / pink hit) so the
          end-screen feels native to the brand. The shareText fallback above
          keeps the emoji form so it pastes cleanly into Slack/iMessage. */}
      <div
        style={{
          marginTop: 22,
          maxWidth: 360,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 6,
        }}>
        {results.length === 0 && (
          <span style={{ fontSize: 22, color: HAUSER_MUTED }}>—</span>
        )}
        {results.slice(0, 30).map((r, i) => {
          // Each dot is colored by the urgency of the issue it represents.
          // Fixed issues render filled; hits render hollow with the same
          // urgency color as the ring — so the grid reads as "what mix
          // of issues did you catch vs. miss" without losing the urgency
          // signal on misses.
          const urgColor = URGENCY_COLOR[r.asteroid.issue.urgency] ?? HAUSER_LIME;
          const isHit = r.lost > 0;
          return (
            <span
              key={i}
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: isHit ? 'transparent' : urgColor,
                border: isHit ? `2px solid ${urgColor}` : 'none',
                boxSizing: 'border-box',
                display: 'inline-block',
              }}
            />
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: HAUSER_MUTED, marginTop: 8 }}>
        {fixedCount} fixed · {hitCount} hit
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          marginTop: 28,
        }}>
        <button
          onClick={onRestart}
          aria-label="Play again"
          style={{
            background: 'transparent',
            color: '#bbb7af',
            border: '1px solid rgba(187,183,175,0.35)',
            borderRadius: 999,
            width: 48,
            height: 48,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M3 12a9 9 0 1 0 3-6.7"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 4v5h5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            color: '#bbb7af',
            border: 'none',
            padding: '0 16px',
            height: 32,
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            lineHeight: 1.2,
          }}>
          Close
        </button>
      </div>
    </Overlay>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────
// Spawn a burst of N particles at (x, y) flying outward in random directions.
const spawnParticles = (
  bucket: Particle[],
  x: number,
  y: number,
  color: string,
  count: number,
) => {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.15 + Math.random() * 0.35;
    bucket.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      color,
      ageMs: 0,
      lifeMs: PARTICLE_LIFE_MS * (0.6 + Math.random() * 0.6),
      size: 2 + Math.random() * 2,
    });
  }
};

// Split a string into lines of at most N words each.
const chunkWords = (s: string, perLine: number): string[] => {
  const words = s.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < words.length; i += perLine) {
    out.push(words.slice(i, i + perLine).join(' '));
  }
  return out.length > 0 ? out : [s];
};

// The Hauser "A" mark — pulled from src/assets/images/hauser-icon.svg.
// The path is defined in a 100×100 viewBox with the A tip at top. We use
// the SVG even-odd fill rule so the inner triangle reads as a cut-out.
const HAUSER_A_PATH = (() => {
  if (typeof Path2D === 'undefined') return null;
  return new Path2D(
    'M56.2133 22.2267H43.7733L31.4266 77.7867H68.56L56.2133 22.2267Z' +
      'M44.2266 67.7067L49.7066 40.3067L49.96 38.32L50.2133 40.3067' +
      'L55.76 67.7067H44.24H44.2266Z',
  );
})();

const asteroidRadius = (a: Asteroid): number =>
  a.baseRadius * (1 + a.ageMs * ASTEROID_GROWTH_FACTOR_PER_MS);

// Proximity-driven escalation: 0 = at spawn edge, 1 = at home. Tier
// climbs from startUrgencyIdx up to Critical as the asteroid closes in.
const effectiveTier = (a: Asteroid, proximity: number): IssueUrgency => {
  const maxClimb = URGENCY_TIERS.length - 1 - a.startUrgencyIdx;
  const climb = Math.min(maxClimb, Math.floor(proximity * (maxClimb + 1)));
  const idx = Math.min(URGENCY_TIERS.length - 1, a.startUrgencyIdx + climb);
  return URGENCY_TIERS[idx];
};

// Cost model: as the asteroid closes in, its "current cost to address" grows
// because deferral compounds. Max damage if it hits home is 2× baseCost.
// Savings = max damage avoided − current cost paid. Catch it early → small
// current cost → big savings. Catch it near home → already escalated →
// small savings (still positive — better than letting it hit).
const HIT_DAMAGE_MULTIPLIER = 2.0;
const currentCost = (a: Asteroid, proximity: number): number =>
  a.baseCost * (1 + (HIT_DAMAGE_MULTIPLIER - 1) * proximity);
const savedReward = (a: Asteroid, proximity: number): number =>
  a.baseCost * HIT_DAMAGE_MULTIPLIER - currentCost(a, proximity);
const hitDamage = (a: Asteroid): number => a.baseCost * HIT_DAMAGE_MULTIPLIER;

const renderFrame = (
  ctx: CanvasRenderingContext2D,
  asteroids: Asteroid[],
  bullets: Bullet[],
  aim: number,
  size: { w: number; h: number },
  shake: number,
  particles: Particle[],
  popups: ScorePopup[],
  callout: ComboCallout | null,
  reserveFrac: number,
  reserveUses: number,
  waveRadius: number,
  waveT: number,
  isMobile: boolean,
  homeY: number,
  spriteScale: number,
) => {
  const { w, h } = size;
  const cx = w / 2;
  const cy = homeY;
  ctx.save();
  if (shake > 0) {
    ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);
  }
  // Background.
  ctx.fillStyle = HAUSER_BG;
  ctx.fillRect(-50, -50, w + 100, h + 100);

  // Starfield.
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  for (let i = 0; i < 100; i++) {
    const x = (i * 137.508) % w;
    const y = (i * 73.219) % h;
    ctx.fillRect(x, y, 1.5, 1.5);
  }

  // Asteroids.
  for (const a of asteroids) {
    const r = asteroidRadius(a) * spriteScale;
    if (a.status === 'fading') {
      const t = (a.fadeMs ?? 0) / 350;
      ctx.strokeStyle = HAUSER_LIME;
      ctx.lineWidth = 3 * (1 - t);
      ctx.globalAlpha = 1 - t;
      ctx.beginPath();
      ctx.arc(a.x, a.y, r * (1 + t * 0.6), 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
      continue;
    }
    const distFromHome = Math.sqrt((a.x - cx) ** 2 + (a.y - cy) ** 2);
    const proximity =
      a.spawnDist > 0 ? Math.max(0, Math.min(1, 1 - distFromHome / a.spawnDist)) : 0;
    const tier = effectiveTier(a, proximity);
    const color = URGENCY_COLOR[tier];
    ctx.fillStyle = `${color}22`;
    ctx.beginPath();
    ctx.arc(a.x, a.y, r + 8, 0, Math.PI * 2);
    ctx.fill();
    // White flash overlay when freshly hit (chips through hp).
    const flash = (a.hitFlashMs ?? 0) / 120;
    ctx.fillStyle = flash > 0 ? `rgba(255,255,255,${0.4 * flash})` : color;
    if (flash > 0) {
      ctx.fillStyle = color;
    }
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.arc(a.x, a.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    if (flash > 0) {
      ctx.fillStyle = `rgba(255,255,255,${flash * 0.45})`;
      ctx.beginPath();
      ctx.arc(a.x, a.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 + Math.min(2.5, a.ageMs / 4000);
    ctx.beginPath();
    ctx.arc(a.x, a.y, r, 0, Math.PI * 2);
    ctx.stroke();
    // Label sits 10px below the asteroid in 10px Manrope, wrapped at
    // 3 words per line so long titles don't sprawl across the canvas.
    ctx.fillStyle = HAUSER_TEXT;
    ctx.font = `600 10px Manrope, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const lines = chunkWords(a.issue.title, 3);
    const lineH = 13;
    const labelTop = a.y + r + 15;
    for (let li = 0; li < lines.length; li++) {
      ctx.fillText(lines[li], a.x, labelTop + li * lineH);
    }
  }

  // Bullets.
  ctx.fillStyle = HAUSER_TEXT; // #f3f3f3
  for (const b of bullets) {
    ctx.beginPath();
    ctx.arc(b.x, b.y, BULLET_R, 0, Math.PI * 2);
    ctx.fill();
  }

  // Particles — explosions / sparks. Fade out over their lifetime.
  for (const p of particles) {
    const t = 1 - p.ageMs / p.lifeMs;
    ctx.globalAlpha = Math.max(0, t);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * Math.max(0.4, t), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Score popups — drift upward, fade out.
  for (const pop of popups) {
    const t = 1 - pop.ageMs / POPUP_LIFE_MS;
    ctx.globalAlpha = Math.max(0, t);
    ctx.fillStyle = pop.color;
    ctx.font = `700 14px Manrope, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pop.text, pop.x, pop.y);
  }
  ctx.globalAlpha = 1;

  // Combo callout — large lime text in the centre, scales in then fades.
  // Mobile shrinks the font significantly so the text doesn't overflow
  // the narrow viewport.
  if (callout) {
    const t = callout.ageMs / CALLOUT_LIFE_MS;
    const grow = Math.min(1, t * 4); // pop in over first 250ms
    const fade = 1 - Math.max(0, (t - 0.4) / 0.6);
    ctx.globalAlpha = Math.max(0, fade);
    const baseSize = isMobile ? 18 : 36;
    const grownBy = isMobile ? 8 : 16;
    const fontSize = baseSize + grow * grownBy;
    ctx.font = `800 ${fontSize}px Manrope, sans-serif`;
    ctx.fillStyle = HAUSER_LIME;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(callout.text, cx, cy - (isMobile ? 70 : 110));
    ctx.globalAlpha = 1;
  }

  // Reserve / Force Field bubble — large faint lime dome around the
  // ship while engaged. Pulses gently so the player feels protected.
  if (waveRadius > 0) {
    // Reserve shockwave — expanding annulus. Bright leading edge that fades
    // outward, with a softer trailing wash that decays as it grows.
    const innerEdge = Math.max(0, waveRadius - RESERVE_WAVE_THICKNESS);
    const trailAlpha = 0.18 * (1 - waveT);
    const edgeAlpha = 0.55 * (1 - waveT * 0.6);

    ctx.save();
    // Trailing wash — a soft fill from the ship out to the leading edge.
    const trail = ctx.createRadialGradient(cx, cy, 0, cx, cy, waveRadius);
    trail.addColorStop(0, `rgba(192,255,2,0)`);
    trail.addColorStop(0.7, `rgba(192,255,2,${(trailAlpha * 0.4).toFixed(3)})`);
    trail.addColorStop(1, `rgba(192,255,2,${trailAlpha.toFixed(3)})`);
    ctx.fillStyle = trail;
    ctx.beginPath();
    ctx.arc(cx, cy, waveRadius, 0, Math.PI * 2);
    ctx.fill();

    // Leading edge — bright lime ring with a glow.
    ctx.beginPath();
    ctx.arc(cx, cy, waveRadius, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(1.5, RESERVE_WAVE_THICKNESS * 0.18);
    ctx.strokeStyle = `rgba(192,255,2,${edgeAlpha.toFixed(3)})`;
    ctx.shadowColor = HAUSER_LIME;
    ctx.shadowBlur = 22;
    ctx.stroke();

    // Inner faint trailing ring.
    if (innerEdge > 0) {
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(cx, cy, innerEdge, 0, Math.PI * 2);
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(192,255,2,${(edgeAlpha * 0.4).toFixed(3)})`;
      ctx.stroke();
    }
    ctx.restore();
  }

  // Ship at centre = the Hauser "A" mark, rotated to point along the aim.
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(aim + Math.PI / 2); // path tip is at top by default
  // The path is 100×100; scale to twice SHIP_SIZE so the rendered A is
  // ~SHIP_SIZE*2 across (matches the previous triangle footprint).
  const aScale = (SHIP_SIZE * 2 * spriteScale) / 100;
  ctx.scale(aScale, aScale);
  ctx.translate(-50, -50); // center the path on origin
  ctx.fillStyle = HAUSER_LIME;
  if (HAUSER_A_PATH) {
    // Even-odd so the inner triangle of the "A" reads as a cut-out.
    ctx.fill(HAUSER_A_PATH, 'evenodd');
  }
  ctx.restore();

  ctx.restore();

  // Full keyboard hint pinned to the bottom of the play area — dim outline
  // of all keys with the spacebar highlighted as the affordance. Skipped
  // on mobile (no keyboard); mobile players get an HTML tap button.
  if (!isMobile) {
    drawKeyboardHint(ctx, cx, h - 78, reserveFrac > 0, reserveUses);
  }
};

// Tiny rounded-rect helper for keyboard caps.
const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const rr = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
};

const drawKeyboardHint = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  active: boolean,
  uses: number,
) => {
  // Layout — 4 rows. Numbers come from a stylized mini-keyboard, sized so
  // the whole thing reads as "a keyboard" at a glance without crowding the
  // ship area. The spacebar in the bottom row is the one highlighted key.
  const keyW = 7;
  const keyH = 7;
  const gap = 1.5;
  const rowGap = 1.5;
  // Rows are described by per-key widths in "keyW units"; 1 = standard,
  // larger values stretch the cap (Tab, Shift, Spacebar, etc.).
  type RowDef = { widths: number[]; offset?: number };
  const rows: RowDef[] = [
    { widths: Array(10).fill(1) }, // number row
    { widths: [1.4, ...Array(9).fill(1)] }, // tab + qwerty-ish
    { widths: [1.7, ...Array(8).fill(1), 1.7] }, // caps + asdf + return
    { widths: [1.2, 1, 5.6, 1, 1.2] }, // shift, alt, SPACE, alt, shift
  ];
  // Compute total width per row (units * keyW + gaps), pick max as canvas width.
  const rowPixelWidth = (r: RowDef): number => {
    const totalUnits = r.widths.reduce((s, u) => s + u, 0);
    const totalKeyPx = totalUnits * keyW;
    const gapPx = (r.widths.length - 1) * gap;
    return totalKeyPx + gapPx;
  };
  const totalH = rows.length * keyH + (rows.length - 1) * rowGap;

  ctx.save();
  ctx.translate(cx, cy);

  // Caption stack above the keyboard: bolt icon centered on top, then
  // "RESERVE" label below it, then a 20px gap before the keyboard.
  const label = active ? 'RESERVE ENGAGED' : uses > 0 ? 'RESERVE' : 'RESERVE EMPTY';
  const labelColor = uses > 0 ? '#F3F3F3' : 'rgba(243,243,243,0.45)';
  const boltColor = active ? HAUSER_LIME : uses > 0 ? HAUSER_LIME : 'rgba(187,183,175,0.7)';

  const RESERVE_TO_KEYBOARD_GAP = 20;
  const labelHeight = 15; // matches font size below
  const boltSize = 28; // 2x of the original 14
  const iconToLabelGap = 10;

  // Anchor the bottom of the label exactly RESERVE_TO_KEYBOARD_GAP above the
  // keyboard's top edge. Label is drawn with textBaseline="middle".
  const keyboardTop = -totalH / 2;
  const labelY = keyboardTop - RESERVE_TO_KEYBOARD_GAP - labelHeight / 2;
  const iconCy = labelY - labelHeight / 2 - iconToLabelGap - boltSize / 2;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Lightning bolt inside a circle, centered above the RESERVE label.
  // Bolt path is the same one used in the EQ Boost button.
  const iconR = boltSize / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(0, iconCy, iconR, 0, Math.PI * 2);
  if (active) {
    ctx.fillStyle = HAUSER_LIME;
    ctx.fill();
    ctx.shadowColor = HAUSER_LIME;
    ctx.shadowBlur = 14;
    ctx.fill();
    ctx.shadowBlur = 0;
  } else {
    ctx.fillStyle = uses > 0 ? 'rgba(192,255,2,0.10)' : 'rgba(255,255,255,0.04)';
    ctx.fill();
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = uses > 0 ? HAUSER_LIME : 'rgba(187,183,175,0.35)';
    ctx.stroke();
  }
  ctx.restore();

  // Bolt path inside the circle.
  ctx.save();
  const innerBoltSize = boltSize * 0.55;
  const boltScale = innerBoltSize / 12;
  ctx.translate(0, iconCy);
  ctx.scale(boltScale, boltScale);
  ctx.translate(-6, -6);
  ctx.beginPath();
  ctx.moveTo(7, 1);
  ctx.lineTo(2, 7);
  ctx.lineTo(5, 7);
  ctx.lineTo(4, 11);
  ctx.lineTo(9, 5);
  ctx.lineTo(6, 5);
  ctx.lineTo(7, 1);
  ctx.closePath();
  ctx.lineWidth = 0.9;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = active ? '#0a0a0a' : boltColor;
  ctx.stroke();
  ctx.restore();

  // RESERVE label, centered, 30% letter spacing.
  ctx.fillStyle = labelColor;
  ctx.font = "800 15px Manrope, sans-serif";
  // 30% of font size → 4.5px tracking. ctx.letterSpacing is the modern API
  // and is widely supported; cast since lib.dom may not type it yet.
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '4.5px';
  ctx.fillText(label, 0, labelY);
  (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0px';

  // Render rows.
  let y = -totalH / 2;
  for (let ri = 0; ri < rows.length; ri++) {
    const row = rows[ri];
    const rowW = rowPixelWidth(row);
    let x = -rowW / 2; // center each row independently
    const isSpaceRow = ri === rows.length - 1;
    for (let ki = 0; ki < row.widths.length; ki++) {
      const u = row.widths[ki];
      const w = u * keyW;
      const isSpace = isSpaceRow && ki === 2; // middle wide cap
      roundRectPath(ctx, x, y, w, keyH, 1.6);
      if (isSpace) {
        if (active) {
          ctx.fillStyle = HAUSER_LIME;
          ctx.fill();
          ctx.shadowColor = HAUSER_LIME;
          ctx.shadowBlur = 12;
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle =
            uses > 0 ? 'rgba(192,255,2,0.18)' : 'rgba(255,255,255,0.05)';
          ctx.fill();
          ctx.strokeStyle =
            uses > 0 ? 'rgba(192,255,2,0.85)' : 'rgba(255,255,255,0.22)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      } else {
        ctx.fillStyle = 'rgba(255,255,255,0.04)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.16)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
      x += w + gap;
    }
    y += keyH + rowGap;
  }

  ctx.restore();

  // Pip indicators for uses remaining, just below the keyboard.
  const pipY = cy + totalH / 2 + 9;
  const pipGap = 7;
  const totalPipsW = (RESERVE_USES_PER_ROUND - 1) * pipGap;
  for (let i = 0; i < RESERVE_USES_PER_ROUND; i++) {
    const px = cx - totalPipsW / 2 + i * pipGap;
    ctx.beginPath();
    ctx.arc(px, pipY, 2.2, 0, Math.PI * 2);
    ctx.fillStyle = i < uses ? HAUSER_LIME : 'rgba(255,255,255,0.18)';
    ctx.fill();
  }
};

export default RepairAsteroidsGame;
